import { useState } from "react";
import {
  BookOpenText,
  ChevronDown,
  ChevronUp,
  ClipboardPaste,
  Plus,
  Trash2,
} from "lucide-react";

import {
  formatReferenceEntry,
  splitReferencePaste,
  type ManuscriptReference,
} from "../domain/manuscript";

type ReferencesEditorProps = {
  references: ManuscriptReference[];
  numbering: "preserve" | "numeric";
  onAdd: () => void;
  onBulkAdd: (items: string[]) => void;
  onChange: (id: string, patch: Partial<Omit<ManuscriptReference, "id">>) => void;
  onMove: (id: string, direction: -1 | 1) => void;
  onDelete: (id: string) => void;
};

export function ReferencesEditor({
  references,
  numbering,
  onAdd,
  onBulkAdd,
  onChange,
  onMove,
  onDelete,
}: ReferencesEditorProps) {
  const [bulkText, setBulkText] = useState("");

  const importBulk = () => {
    const items = splitReferencePaste(bulkText);
    if (!items.length) return;
    onBulkAdd(items);
    setBulkText("");
  };

  return (
    <main className="editor-panel">
      <div className="editor-hero">
        <div className="hero-icon">
          <BookOpenText size={22} />
        </div>
        <div>
          <span className="eyebrow">REFERENCES</span>
          <h1>参考文献</h1>
          <p>逐条保存真实引文；默认保留原格式，编号与悬挂缩进由当前模板控制。</p>
        </div>
      </div>

      <section className="editor-card entity-editor-card references-editor-card">
        <div className="reference-import-box">
          <div>
            <ClipboardPaste size={17} />
            <span>
              <strong>批量粘贴</strong>
              <small>优先用空行分隔；连续编号的条目也可每条一行。普通换行按同一条引文保留。</small>
            </span>
          </div>
          <textarea
            aria-label="批量粘贴参考文献"
            onChange={(event) => setBulkText(event.target.value)}
            placeholder={"1. Reference one...\n2. Reference two..."}
            rows={4}
            value={bulkText}
          />
          <div className="reference-import-actions">
            <button disabled={!bulkText.trim()} onClick={importBulk} type="button">
              导入粘贴内容
            </button>
            <button onClick={onAdd} type="button">
              <Plus size={14} /> 添加空白条目
            </button>
          </div>
        </div>

        {references.length ? (
          <div className="entity-list reference-list">
            {references.map((reference, index) => (
              <article className="entity-card reference-card" key={reference.id}>
                <div className="entity-card-heading">
                  <div className="reference-card-title">
                    <span className="position-badge">{index + 1}</span>
                    <strong>参考文献 {index + 1}</strong>
                  </div>
                  <div className="entity-actions">
                    <button
                      aria-label={`上移参考文献 ${index + 1}`}
                      disabled={index === 0}
                      onClick={() => onMove(reference.id, -1)}
                      type="button"
                    >
                      <ChevronUp size={14} />
                    </button>
                    <button
                      aria-label={`下移参考文献 ${index + 1}`}
                      disabled={index === references.length - 1}
                      onClick={() => onMove(reference.id, 1)}
                      type="button"
                    >
                      <ChevronDown size={14} />
                    </button>
                    <button
                      aria-label={`删除参考文献 ${index + 1}`}
                      className="danger-icon"
                      onClick={() => onDelete(reference.id)}
                      type="button"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                <label>
                  <span className="field-label">完整引文文本</span>
                  <textarea
                    aria-label={`参考文献 ${index + 1} 完整文本`}
                    onChange={(event) =>
                      onChange(reference.id, { text: event.target.value })
                    }
                    placeholder="Paste the exact formatted reference"
                    rows={3}
                    value={reference.text}
                  />
                </label>
                <div className="form-grid three reference-identifiers">
                  <label>
                    <span className="field-label">
                      DOI <small>可选</small>
                    </span>
                    <input
                      aria-label={`参考文献 ${index + 1} DOI`}
                      onChange={(event) =>
                        onChange(reference.id, { doi: event.target.value })
                      }
                      placeholder="10.xxxx/xxxxx"
                      value={reference.doi}
                    />
                  </label>
                  <label>
                    <span className="field-label">
                      PMID <small>可选</small>
                    </span>
                    <input
                      aria-label={`参考文献 ${index + 1} PMID`}
                      onChange={(event) =>
                        onChange(reference.id, { pmid: event.target.value })
                      }
                      placeholder="12345678"
                      value={reference.pmid}
                    />
                  </label>
                  <label>
                    <span className="field-label">
                      URL <small>可选</small>
                    </span>
                    <input
                      aria-label={`参考文献 ${index + 1} URL`}
                      onChange={(event) =>
                        onChange(reference.id, { url: event.target.value })
                      }
                      placeholder="https://..."
                      value={reference.url}
                    />
                  </label>
                </div>
                <p className="reference-output-preview">
                  <span>输出预览</span>
                  {formatReferenceEntry(reference, index, numbering) || "尚未填写内容"}
                </p>
              </article>
            ))}
          </div>
        ) : (
          <div className="empty-entity-state">
            尚未添加参考文献。可批量粘贴，也可逐条添加 DOI、PMID 或 URL。
          </div>
        )}
      </section>
    </main>
  );
}
